import type { Edge, Node } from '@xyflow/react';
import i18next from '../../i18n/initI18n';
import type { MailingCampaign, MailingChannel } from './mailingTypes';
import { defaultChannelNodeData, type ChannelNodeData, type StartNodeData } from './mailingTypes';

function generateId(): string {
  return `mc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyFlow(): { nodes: Node[]; edges: Edge[] } {
  const startId = 'node-start';
  const endId = 'node-end';
  const smartEdge = (id: string, source: string, target: string) =>
    ({
      id,
      source,
      target,
      sourceHandle: 'out',
      targetHandle: 'in',
      type: 'smart',
    }) satisfies Edge;

  return {
    nodes: [
      {
        id: startId,
        type: 'mailingStart',
        position: { x: 80, y: 120 },
        data: {
          trigger: 'manual' as const,
          scheduleDate: '',
          scheduleTime: '',
        } satisfies StartNodeData,
        deletable: false,
      },
      {
        id: endId,
        type: 'mailingEnd',
        position: { x: 300, y: 120 },
        data: {},
        deletable: false,
      },
    ],
    edges: [smartEdge(`e-${startId}-${endId}`, startId, endId)],
  };
}

export function createCampaign(name: string): MailingCampaign {
  const { nodes, edges } = createEmptyFlow();
  const now = Date.now();
  return {
    id: generateId(),
    name: name.trim() || i18next.t('common.unnamed'),
    createdAt: now,
    updatedAt: now,
    nodes,
    edges,
  };
}

/** Линейная цепочка каналов от старта до конца (для шаблонов). */
export function createCampaignFromChannelChain(name: string, channels: MailingChannel[]): MailingCampaign {
  if (!channels.length) {
    return createCampaign(name);
  }
  const startId = 'node-start';
  const endId = 'node-end';
  const x0 = 80;
  const dx = 220;
  const y = 120;
  const ts = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
  const channelIds = channels.map((_, i) => `n-ch-${ts}-${i}`);
  const nodes: Node[] = [
    {
      id: startId,
      type: 'mailingStart',
      position: { x: x0, y },
      data: {
        trigger: 'manual' as const,
        scheduleDate: '',
        scheduleTime: '',
      } satisfies StartNodeData,
      deletable: false,
    },
    ...channels.map((ch, i) => ({
      id: channelIds[i],
      type: 'mailingChannel' as const,
      position: { x: x0 + dx * (i + 1), y },
      data: defaultChannelNodeData(ch) as ChannelNodeData,
    })),
    {
      id: endId,
      type: 'mailingEnd',
      position: { x: x0 + dx * (channels.length + 1), y },
      data: {},
      deletable: false,
    },
  ];

  const chain = [startId, ...channelIds, endId];
  const edges: Edge[] = [];
  for (let i = 0; i < chain.length - 1; i += 1) {
    const a = chain[i];
    const b = chain[i + 1];
    edges.push({
      id: `e-${a}-${b}-${i}`,
      source: a,
      target: b,
      sourceHandle: 'out',
      targetHandle: 'in',
      type: 'smart',
    });
  }

  const now = Date.now();
  return {
    id: generateId(),
    name: name.trim() || i18next.t('common.unnamed'),
    createdAt: now,
    updatedAt: now,
    nodes,
    edges,
  };
}

export function upsertCampaign(list: MailingCampaign[], campaign: MailingCampaign): MailingCampaign[] {
  const i = list.findIndex((c) => c.id === campaign.id);
  const now = Date.now();
  const prev = i >= 0 ? list[i] : undefined;
  const next: MailingCampaign = {
    ...campaign,
    updatedAt: now,
    createdAt: campaign.createdAt ?? prev?.createdAt ?? now,
  };
  if (i >= 0) {
    const copy = [...list];
    copy[i] = next;
    return copy;
  }
  return [...list, next];
}

export function deleteCampaign(list: MailingCampaign[], id: string): MailingCampaign[] {
  return list.filter((c) => c.id !== id);
}
